"use strict"

import React, { useState } from "react"
import { Button } from "react-bootstrap"

import { isLTPM } from "../../../../components/shared_components_registration"
import { withZoomState } from "../../../config/ZoomConfigProvider"
import { withPlotSettingsConfig } from "../../../config/PlotSettingsConfigProvider"
import { ZoomInButton, ZoomOutButton } from "../ZoomControlsPopover"
import { getCurrentXRange } from "./utils"
import XAxisRangeInputs from "./XAxisRangeInputs"
import YAxesRangeInputs from "./YAxesRangeInputs"
import ZoomThumbnail from "./ZoomThumbnail"
import { isNumber } from "../../../utils"

const registration = {
  zoom_preset_btn_title: (window, intl) =>
    isLTPM
      ? intl.formatMessage({ id: "graphing.zoomControls.day.window.title" }, { count: window })
      : `${window} Day Zoom Window`,
  x_y_zoom_all: (intl) =>
    isLTPM
      ? intl.formatMessage({
          id: "graphing.zoomControls.zoom.all.y.x.axis",
        })
      : "X & Y-Zoom all plots of this type?",
  y_zoom_all: (intl) =>
    isLTPM
      ? intl.formatMessage({
          id: "graphing.zoomControls.zoom.all.y.axis",
        })
      : "Y-Zoom all plots of this type?",
  manually_set_axis_label: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.set.axis.range" }) : "Set Y-Axes Ranges Manually?",
  clear_text: (intl) => (isLTPM ? intl.formatMessage({ id: "general.clearButton" }) : "Clear"),
  zoom_disabled_message: (intl) =>
    isLTPM
      ? intl.formatMessage({
          id: "graphing.zoomControls.manual.axis.disabled",
        })
      : "Disabled (Zoom Active)",
  graph_types_allowed: isLTPM ? [] : ["rnaseq"],
  day_label: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.timelabel.day" }) : "Day"),
  submit_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.submitButton" }) : "Submit"),
}

export const ZoomScrollButton = ({ intl, label, zoomRanges, zoomConfig }) => {
  const [rawMinTime, rawMaxTime] = zoomRanges.time || []
  const zoomDiff =
    isNumber(rawMinTime) && isNumber(rawMaxTime) ? +Math.abs(rawMaxTime - rawMinTime).toFixed(2) : undefined
  const buttonTitle = `${label === "<" ? "-" : "+"} ${zoomDiff} ${registration.day_label(intl)}`
  return (
    <>
      {zoomDiff && (
        <Button
          className={"btn-sm"}
          style={{ padding: "2px 4px 2px 4px" }}
          variant={"primary"}
          title={buttonTitle}
          onClick={() => {
            const valueChange = label === "<" ? zoomDiff * -1 : zoomDiff
            if (isNumber(rawMinTime) && isNumber(rawMaxTime)) {
              zoomConfig.zoomTime(rawMinTime + valueChange, rawMaxTime + valueChange)
            }
          }}
        >
          <span style={{ fontSize: "80%" }}>
            {label === "<" ? `${label}  ${buttonTitle}` : `${buttonTitle}  ${label}`}
          </span>
        </Button>
      )}
    </>
  )
}

/*
 * List of preset time intervals (in days) to offer in zoom controls.
 */
const zoomPresets = [1.0, 7.0, 30.0]

const ZoomControls = (props) => {
  const {
    timeExtractor, // From PlotSettingsConfigProvider
    axesAndRanges,
    zoomKey,
    graphKey,
    allowIndependentGraphZoom = false,
    universalXZoomOnly = false,
    graphIndex,
    plotConfigUpdater,
    baseTime,
    intl,

    minTime, // From ZoomConfigProvider
    maxTime, // From ZoomConfigProvider
    zoomConfig, // From ZoomConfigProvider
    run_accessor, // From ZoomConfigProvider
  } = props

  const zoomRanges = universalXZoomOnly
    ? !isNaN(minTime) && !isNaN(maxTime)
      ? { time: [minTime, maxTime] }
      : {}
    : zoomConfig.zoomForPlot(zoomKey, graphKey)

  const isIndependent = () => {
    return zoomRanges[run_accessor] !== undefined
  }

  const toggleIndependentGraphZoom = (ev) => {
    if (ev.target.checked) {
      zoomConfig.zoomRunCollectively(graphKey, zoomKey)
    } else {
      zoomConfig.zoomRunIndependently(graphKey, zoomKey)
    }
  }

  const [minTimeSpan, maxTimeSpan] = axesAndRanges["time"] || [],
    [rawMinTime, rawMaxTime] = zoomRanges.time || []
  const fullMinTime =
      minTimeSpan !== undefined &&
      rawMinTime !== undefined &&
      timeExtractor.offsetDays(minTimeSpan, rawMinTime),
    fullMaxTime =
      minTimeSpan !== undefined &&
      rawMaxTime !== undefined &&
      timeExtractor.offsetDays(minTimeSpan, rawMaxTime)
  const tag_category =
    graphKey?.special === "site-wide"
      ? "tags"
      : graphKey?.special === "microbial-community"
        ? "microbes"
        : "observations"
  const observations = graphKey ? graphKey[tag_category] : []

  const doUpdateZoomWindow = (window) => {
    let minWindow = fullMinTime || minTimeSpan
    if (fullMinTime && fullMinTime < minTimeSpan) {
      minWindow = minTimeSpan
    }
    let maxWindow = timeExtractor.min(timeExtractor.offsetDays(minWindow, window), maxTimeSpan)
    minWindow = timeExtractor.min(minWindow, timeExtractor.offsetDays(maxWindow, -window))

    zoomConfig.zoomTime(
      timeExtractor.difference(minWindow, minTimeSpan),
      timeExtractor.difference(maxWindow, minTimeSpan),
    )
  }

  const ZoomPresetButton = (window) => (
    <Button
      key={window}
      className={"btn-sm"}
      variant={"outline-primary"}
      title={registration.zoom_preset_btn_title(window, intl)}
      disabled={timeExtractor.difference(maxTimeSpan, minTimeSpan) < window}
      onClick={(_) => doUpdateZoomWindow(window)}
    >
      {window}
    </Button>
  )

  const y_axis_props = {
    intl: intl,
    zoomRanges: zoomRanges,
    graphKey: graphKey,
    graphIndex: graphIndex,
    plotConfigUpdater: plotConfigUpdater,
  }

  const [dayWindow, setDayWindow] = useState(0.5)

  return (
    <>
      <div style={{ textAlign: "center" }}>
        {axesAndRanges["time"] && (
          <ZoomScrollButton intl={intl} label={"<"} zoomRanges={zoomRanges} zoomConfig={zoomConfig} />
        )}
        <ZoomThumbnail
          timeExtractor={timeExtractor}
          axesAndRanges={axesAndRanges}
          zoomRanges={zoomRanges}
          zoomKey={zoomKey}
          graphKey={graphKey}
          zoomConfig={zoomConfig}
          baseTime={baseTime || minTimeSpan}
        />
        {axesAndRanges["time"] && (
          <ZoomScrollButton intl={intl} label={">"} zoomRanges={zoomRanges} zoomConfig={zoomConfig} />
        )}
      </div>
      <div style={{ textAlign: "center", marginTop: "10px" }}>
        <div style={{ display: "flex" }}>
          <ZoomInButton
            intl={intl}
            customOnClick={() => {
              const rootNode = document.getElementById("application-page")
              rootNode.click() // To close OverlayTrigger and move it out of the way for zooming plot click
            }}
          />
          <ZoomOutButton intl={intl} />
          {/* Only meaningful to show these for time-based graphs */}
          {axesAndRanges["time"] && (
            <>
              <>{zoomPresets.map((zoomInterval) => ZoomPresetButton(zoomInterval))}</>
              <div>
                <div>
                  <input
                    type={"number"}
                    onWheel={(e) => e.target.blur()}
                    style={{ width: "50px", fontSize: "75%" }}
                    step={"1.0"}
                    value={isNumber(dayWindow) ? dayWindow : ""}
                    onChange={(e) => {
                      const maxAllowedSpan = +timeExtractor.difference(maxTimeSpan, minTimeSpan).toFixed(2)
                      const value = isNumber(e.target.value) ? +e.target.value : e.target.value
                      if (isNumber(value) && value > maxAllowedSpan) {
                        setDayWindow(maxAllowedSpan)
                      } else if (isNumber(value) && value <= 0.04) {
                        setDayWindow(0.04)
                      } else {
                        setDayWindow(value)
                      }
                    }}
                  />
                </div>
                <div>
                  <Button
                    variant={"outline-primary"}
                    className={"btn btn-sm"}
                    disabled={
                      !isNumber(dayWindow) || timeExtractor.difference(maxTimeSpan, minTimeSpan) < dayWindow
                    }
                    style={{ padding: "2px" }}
                    title={registration.zoom_preset_btn_title(dayWindow || "", intl)}
                    onClick={(_) => {
                      doUpdateZoomWindow(dayWindow)
                    }}
                  >
                    {registration.submit_label(intl)}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
        {allowIndependentGraphZoom && (
          <div>
            <label style={{ marginTop: "10px" }}>
              <input type="checkbox" checked={!isIndependent()} onChange={toggleIndependentGraphZoom} />
              &nbsp;
              {graphKey && graphKey.special === "obs-vs-obs" && (
                <span>{registration.x_y_zoom_all(intl)}</span>
              )}
              {graphKey && graphKey.special !== "obs-vs-obs" && <span>{registration.y_zoom_all(intl)}</span>}
            </label>
          </div>
        )}
      </div>

      {graphKey &&
        (!graphKey.special ||
          [
            ...["multi", "site-wide", "obs-vs-obs", "microbial-community"],
            ...registration.graph_types_allowed,
          ].includes(graphKey.special)) && (
          <>
            <hr style={{ marginTop: "10px", marginBottom: "10px" }} />
            <div style={{ marginBottom: "10px" }}>
              <div style={{ marginBottom: "5px" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <label>{registration.manually_set_axis_label(intl)}:</label>
                  &nbsp;
                  <Button
                    title={registration.clear_text(intl)}
                    variant={"outline-primary"}
                    style={{ marginLeft: "auto" }}
                    className={"btn btn-sm"}
                    onClick={(_) => {
                      delete graphKey.axis1domain
                      delete graphKey.axis2domain
                      delete graphKey["xaxisdomain"]
                      plotConfigUpdater(graphKey, graphIndex)
                    }}
                    disabled={!graphKey.axis1domain && !graphKey.axis2domain && !graphKey["xaxisdomain"]}
                  >
                    <span>{registration.clear_text(intl)}</span>
                  </Button>
                </div>
                {(zoomRanges.y || zoomRanges.y2) && (
                  <p style={{ color: "var(--lanzalake-grey)" }}>
                    *{registration.zoom_disabled_message(intl)}
                  </p>
                )}
              </div>
              <YAxesRangeInputs
                {...y_axis_props}
                disabled={observations.filter((o) => !o.axis2).length === 0}
                axis_number={"1"}
                axis={"Y"}
              />
              {graphKey && graphKey.special !== "obs-vs-obs" && (
                <YAxesRangeInputs
                  {...y_axis_props}
                  disabled={observations.filter((o) => o.axis2).length === 0}
                  axis_number={"2"}
                  axis={"Y"}
                />
              )}
              {graphKey && graphKey.special === "obs-vs-obs" ? (
                <YAxesRangeInputs {...y_axis_props} disabled={false} axis_number={"1"} axis={"X"} />
              ) : (
                <>
                  <hr style={{ marginTop: "10px", marginBottom: "10px" }} />
                  <XAxisRangeInputs
                    timeExtractor={timeExtractor}
                    intl={intl}
                    zoomConfig={zoomConfig}
                    baseTime={baseTime || minTimeSpan}
                    xaxisdomain={getCurrentXRange(
                      axesAndRanges,
                      zoomRanges,
                      timeExtractor,
                      baseTime || minTimeSpan,
                    )}
                  />
                </>
              )}
            </div>
          </>
        )}
    </>
  )
}

export default withPlotSettingsConfig(withZoomState(ZoomControls))
